import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  Badge,
  Box,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import { Loading } from '@bumble/app/components/Loading';
import { useApi } from '@bumble/app/hooks/use-api';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import { Indicator } from '@bumble/app/components/Indicator';
import MultipleSelections from '@bumble/app/components/MultipleSelections';
import { CellularDiagnostics, EditDataForwarding, EditStaticIp, AssignSharedDataPool, UnassignSharedDataPool, EditDataUsageResetDay } from '@bumble/app/components/cellular-device';
import { apiError } from '@bumble/app/utils/api-error';
import DialogStandard from '@bumble/app/components/DialogStandard';
import CellularStats from '@bumble/app/components/CellularStats';

interface CellularTrafficLimitStatus {
  enabled: boolean;
}

export const CellularWAN = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  const [entities, setEntities] = useState<any[]>([{ label: 'None', value: 'none' }]);
  const [entity, setEntity] = useState<any>(entities[0]);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [showEditDataModal, setShowEditDataModal] = useState<boolean>(false);
  const [showEditStaticIpModal, setShowEditStaticIpModal] = useState<boolean>(false);
  const [showDiagnosticDialog, setShowDiagnosticDialog] = useState<boolean>(false);
  const [showAssignDataPoolModal, setShowAssignDataPoolModal] = useState<boolean>(false);
  const [showUnassignDataPoolModal, setShowUnassignDataPoolModal] = useState<boolean>(false);
  const [showResetDayModal, setShowResetDayModal] = useState<boolean>(false);
  const [menuItems, setMenuItems] = useState<{
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    hasDivider?: boolean;
    disabled?: boolean;
  }[]>([]);

  const infoURL = 'https://www.bumblebeenet.com/post/cellular-wan-faq';

  const selectedCpeId = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);

  const { data: entityNames } = useQuery(
    ['prepareEntityNames'],
    api.getEntityNamesApiV1GetEntityNamesGet.bind(null, {}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    const entity_data = entityNames?.data;
    let actualEntities = [{ label: 'None', value: 'none' }];
    if (entity_data && entity_data.length) {
      actualEntities = actualEntities.concat(entity_data.map((e: string) => ({
        label: e,
        value: e,
      })));
    }
    setEntities(actualEntities);
    setEntity(actualEntities[0]);
  }, [entityNames]);

  const changeEntity = (val: any) => {
    setEntity(val);
    setRowSelectionModel([]);
  };

  const {
    isLoading: loadingCpes,
    data: cpeData,
    refetch: cpeListRefetch,
    isRefetching: refetchingCpeList,
  } = useQuery(
    ['cellularRouters', entity],
    api.getCpeCellularApiV1GetCpeCellularGet.bind(
      null,
      entity?.value && entity.value !== 'none' ? { entity_name: entity.value } : {}
    ),
    { enabled: true },
  );

  const {
    isLoading: loadingCellularStatus,
    data: cellularStatusData,
    refetch: cellularStatusRefetch,
    isRefetching: refetchingCpeStatus,
  } = useQuery(
    ['cellular-status', selectedCpeId],
    () => {
      return api.getCellularStatusApiV1GetCellularStatusGet({
        cpe_id: selectedCpeId,
      });
    },
    {
      enabled: !!selectedCpeId,
      retry: false,
      onError: (error: any) => {
        if (error.message !== 'No CPE selected') {
          apiError(error);
        }
      },
    }
  );

  const {
    data: trafficLimitData,
    refetch: trafficLimitRefetch,
    isRefetching: refetchingTrafficLimitStatus,
  } = useQuery(
    ['cellular-traffic-limit-status', selectedCpeId],
    () =>
      api.request<CellularTrafficLimitStatus, unknown>({
        path: '/get-cellular-traffic-limit-status',
        method: 'GET',
        query: { cpe_id: selectedCpeId },
        secure: true,
        format: 'json',
      }),
    {
      enabled: !!selectedCpeId,
      retry: false,
      onError: apiError,
    },
  );

  const assignedDataPoolName = useMemo(() => {
    if (!selectedCpeId) return '';
    const cpe = cpeData?.data.find((item) => item.cpe_id === selectedCpeId);
    return cpe?.data_pool_name ?? '';
  }, [cpeData, selectedCpeId]);

  const listColumns: GridColDef[] = [
    {
      field: 'cpe_name',
      headerName: 'Name',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'entity_name',
      headerName: 'Entity Name',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'op_state',
      headerName: 'Op State',
      width: 150,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {params.value !== undefined ? <Indicator active={params.value === 'up'} /> : 'N/A'}
        </Box>
      ),
    },
    {
      field: 'active_uplink',
      headerName: 'Active WAN',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'data_pool_name',
      headerName: 'Assigned Data Pool',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'monthly_data_usage_gb',
      headerName: 'Monthly Data Usage (GB)',
      width: 200,
      disableColumnMenu: true,
      valueFormatter: (value) => (value != null ? Number(value).toFixed(3) : ''),
    },
    {
      field: 'data_usage_reset_day',
      headerName: 'Data Reset Day',
      width: 150,
      disableColumnMenu: true,
      // While the CPE is in a pool, the pool's reset day governs, so the CPE's
      // own reset day only applies to pool-less CPEs.
      renderCell: (params) =>
        params.row.data_pool_id ? '—' : (params.value ?? ''),
    },
    {
      field: 'cellular_static_ip',
      headerName: 'Bumblebee Static IP',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'cpe_id',
      headerName: 'Device ID',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'prod_code',
      headerName: 'Product Code',
      width: 150,
      disableColumnMenu: true,
    },
  ];

  const detailColumns: GridColDef[] = [
    {
      field: 'operator_name',
      headerName: 'Operator Name',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'static_ip_address',
      headerName: 'Bumblebee Static IP',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'data_pool_name',
      headerName: 'Assigned Data Pool',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'throttling',
      headerName: 'CPE Throttled',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'signal_quality_pct',
      headerName: 'Signal Quality Percent (%)',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'imei',
      headerName: 'IMEI',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'iccid',
      headerName: 'ICCID',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'modem_model',
      headerName: 'Modem Model',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'ip_address',
      headerName: 'IP Address',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'default_gateway',
      headerName: 'Default Gateway',
      width: 250,
      disableColumnMenu: true,
    },
    {
      field: 'dns_servers',
      headerName: 'DNS Servers',
      flex: 1,
      minWidth: 200,
      disableColumnMenu: true,
    },
    {
      field: 'apn',
      headerName: 'Access Point Name (APN)',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'rat',
      headerName: 'Access Tech',
      width: 130,
      disableColumnMenu: true,
    },
  ];

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection.slice(-1));
  };

  useEffect(() => {
    const items = [
      {
        label: 'Edit Data Forwarding',
        icon: <EditIcon color="info" fontSize="small" />,
        onClick: () => {
          setShowEditDataModal(true);
        },
      },
      {
        label: 'Edit Bumblebee Static IP',
        icon: <EditIcon color="info" fontSize="small" />,
        onClick: () => {
          setShowEditStaticIpModal(true);
        },
      },
      {
        label: 'Get Diagnostics',
        icon: <TroubleshootIcon color="info" fontSize="small" />,
        onClick: () => {
          setShowDiagnosticDialog(true);
        },
      },
      {
        label: 'Assign Shared Data Pool',
        icon: <LinkIcon color="info" fontSize="small" />,
        onClick: () => {
          setShowAssignDataPoolModal(true);
        },
      },
      {
        label: 'Unassign Shared Data Pool',
        icon: <LinkOffIcon color="info" fontSize="small" />,
        onClick: () => {
          setShowUnassignDataPoolModal(true);
        },
      },
      {
        label: 'Set Data Usage Reset Day',
        icon: <CalendarMonthIcon color="info" fontSize="small" />,
        onClick: () => {
          setShowResetDayModal(true);
        },
        hasDivider: true,
      },
    ];
    setMenuItems([...items]);
  }, [selectedCpeId]);

  const handleDataRefetch = () => {
    cpeListRefetch();
    if (selectedCpeId) {
      cellularStatusRefetch();
      trafficLimitRefetch();
    }
    toast.success('Refreshed devices.');
  };

  const handleDataPoolChange = () => {
    queryClient.invalidateQueries(['cellularRouters']);
    queryClient.invalidateQueries(['dataPools']);
    cellularStatusRefetch();
    trafficLimitRefetch();
  };

  return (
    <PageContent>
      <PageHeader
        pageName="Cellular WAN"
        count={cpeData?.data?.length || 0}
      />
      <Box sx={{ mb: 5, position: 'absolute', top: 140, left: 570, display: 'inline-flex', gap: 2 }}>
        <Select options={entities} defaultValue={entities[0]} value={entity} onChange={changeEntity} styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              borderRadius: '8px',
              width: '300px',
            }),
            menu: (baseStyles) => ({
              ...baseStyles,
              width: '300px',
            }),
        }}/>
        <Badge badgeContent={0} color="secondary">
          <Box
            component="a"
            href={infoURL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Tooltip arrow title="Info">
              <IconButton color="primary" size="small">
                <InfoOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Badge>
      </Box>
      {loadingCpes ? (
        <Loading />
      ) : (
        <>
          <Card elevation={0}>
            <CardContent>
              <DataTableGrid
                rows={
                  cpeData?.data.map((item) => ({
                    ...item,
                    id: item.cpe_id,
                  })) || []
                }
                columns={listColumns}
                rowSelectionModel={rowSelectionModel}
                handleSelectionChange={handleSelectionChange}
                itemsSelected={rowSelectionModel.length === 1}
                refreshData={handleDataRefetch}
                isRefetching={refetchingCpeList || refetchingCpeStatus || refetchingTrafficLimitStatus}
                menuItems={menuItems}
                pagination={true}
              />
            </CardContent>
          </Card>
          <Card elevation={0} sx={{ mt: 5 }}>
            <CardContent>
              {selectedCpeId ? (
                <>
                  {loadingCellularStatus ? (
                    <Loading />
                  ) : (
                    <DataTableGrid
                      rows={
                        cellularStatusData?.data
                          ? [
                              {
                                ...cellularStatusData.data,
                                throttling: trafficLimitData?.data == null ? '' : trafficLimitData.data.enabled ? 'Yes' : 'No',
                                dns_servers: cellularStatusData.data.dns_servers?.join(', ') ?? '',
                                data_pool_name: assignedDataPoolName,
                                id: 1
                              },
                            ]
                          : []
                      }
                      columns={detailColumns}
                      hideSearchBar={true}
                    />
                  )}
                  <Box sx={{ mt: 5 }}>
                    <CellularStats id={selectedCpeId} />
                  </Box>
                </>
              ) : (
                <MultipleSelections count={0} />
              )}
            </CardContent>
          </Card>
        </>
      )}
      {showEditDataModal && (
        <DialogStandard
          open={showEditDataModal}
          handleClose={() => {
            setShowEditDataModal(false)
          }}
          title="Edit Data Forwarding"
        >
          <EditDataForwarding
            onSubmit={() => {
              setShowEditDataModal(false)
              cellularStatusRefetch();
            }}
            cpeId={selectedCpeId}
          />
        </DialogStandard>
      )}
      {showEditStaticIpModal && (
        <DialogStandard
          open={showEditStaticIpModal}
          handleClose={() => {
            setShowEditStaticIpModal(false)
          }}
          title="Enable or Disable Bumblebee Static IP"
        >
          <EditStaticIp
            onSubmit={() => {
              setShowEditStaticIpModal(false)
              cellularStatusRefetch();
            }}
            cpeId={selectedCpeId}
          />
        </DialogStandard>
      )}
      {showDiagnosticDialog && selectedCpeId && (
        <DialogStandard
          size="md"
          open={showDiagnosticDialog}
          handleClose={() => setShowDiagnosticDialog(false)}
          title="Cellular Diagnostics"
        >
          <CellularDiagnostics
            cpeId={selectedCpeId}
            onSubmit={() => setShowDiagnosticDialog(false)}
          />
        </DialogStandard>
      )}
      {showAssignDataPoolModal && selectedCpeId && (
        <DialogStandard
          open={showAssignDataPoolModal}
          handleClose={() => setShowAssignDataPoolModal(false)}
          title="Assign Shared Data Pool"
        >
          <AssignSharedDataPool
            cpeId={selectedCpeId}
            onSubmit={() => {
              setShowAssignDataPoolModal(false);
              handleDataPoolChange();
            }}
          />
        </DialogStandard>
      )}
      {showUnassignDataPoolModal && selectedCpeId && (
        <DialogStandard
          open={showUnassignDataPoolModal}
          handleClose={() => setShowUnassignDataPoolModal(false)}
          title="Unassign Shared Data Pool"
        >
          <UnassignSharedDataPool
            cpeId={selectedCpeId}
            onSubmit={() => {
              setShowUnassignDataPoolModal(false);
              handleDataPoolChange();
            }}
          />
        </DialogStandard>
      )}
      {showResetDayModal && selectedCpeId && (
        <DialogStandard
          open={showResetDayModal}
          handleClose={() => setShowResetDayModal(false)}
          title="Set Data Usage Reset Day"
        >
          <EditDataUsageResetDay
            cpeId={selectedCpeId}
            onSubmit={() => {
              setShowResetDayModal(false);
              cpeListRefetch();
            }}
          />
        </DialogStandard>
      )}
    </PageContent>
  );
};
